import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  FaClipboardCheck,
  FaFilter,
  FaFileExcel,
  FaRedo,
  FaSearch,
  FaUserShield,
} from "react-icons/fa";
import { db } from "../../Firebase/firebaseconfig";
import { useUserContext } from "../GestionDeUsuarios/UserContext";
import { toJsDate } from "../../helpers/fechas";
import "./Auditoria.css";

const formatDate = (value) => {
  const date = toJsDate(value);
  if (!date) return "Sin fecha";

  return date.toLocaleString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getModule = (accion = "") => accion.split(".")[0] || "general";

const safeText = (value) => (value || "").toString().toLowerCase().trim();

const AuditDetail = ({ label, value }) => {
  if (!value) return null;

  return (
    <details className="audit-details">
      <summary>{label}</summary>
      <pre>{JSON.stringify(value, null, 2)}</pre>
    </details>
  );
};

const Auditoria = () => {
  const { can, user } = useUserContext();
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [moduleFilter, setModuleFilter] = useState("todos");
  const [actionFilter, setActionFilter] = useState("todos");
  const [actorFilter, setActorFilter] = useState("todos");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [entidadTipoFiltro, setEntidadTipoFiltro] = useState("todos");
  const [entidadIdFiltro, setEntidadIdFiltro] = useState("");

  const puedeVerAuditoria = can("auditoria.ver") || user?.rol === "admin";

  const cargarAuditoria = async () => {
    if (!puedeVerAuditoria) return;

    setLoading(true);
    setError("");

    try {
      const q = query(collection(db, "auditoria"), orderBy("fecha", "desc"), limit(250));
      const snap = await getDocs(q);
      setRegistros(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      console.error("Error cargando auditoria:", err);
      setError("No se pudo cargar la auditoria. Revisa permisos o indices de Firestore.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarAuditoria();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puedeVerAuditoria]);

  const modules = useMemo(() => {
    const set = new Set(registros.map((registro) => getModule(registro.accion)));
    return Array.from(set).sort();
  }, [registros]);

  const actions = useMemo(() => {
    const set = new Set(
      registros
        .filter((registro) => moduleFilter === "todos" || getModule(registro.accion) === moduleFilter)
        .map((registro) => registro.accion || "sin_accion")
    );
    return Array.from(set).sort();
  }, [moduleFilter, registros]);

  const actors = useMemo(() => {
    const set = new Set(registros.map((registro) => registro.actorEmail || registro.actorUid).filter(Boolean));
    return Array.from(set).sort();
  }, [registros]);

  const entidadTipos = useMemo(() => {
    const set = new Set(registros.map((r) => r.entidadTipo).filter(Boolean));
    return Array.from(set).sort();
  }, [registros]);

  const filtered = useMemo(() => {
    const term = safeText(search);

    return registros.filter((registro) => {
      const moduleName = getModule(registro.accion);
      const fecha = toJsDate(registro.fecha);
      const matchesModule = moduleFilter === "todos" || moduleName === moduleFilter;
      const matchesAction = actionFilter === "todos" || registro.accion === actionFilter;
      const matchesActor =
        actorFilter === "todos" ||
        registro.actorEmail === actorFilter ||
        registro.actorUid === actorFilter;
      const matchesStart =
        !fechaInicio || (fecha && fecha >= new Date(`${fechaInicio}T00:00:00`));
      const matchesEnd =
        !fechaFin || (fecha && fecha <= new Date(`${fechaFin}T23:59:59`));
      const haystack = safeText(
        [
          registro.accion,
          registro.entidadTipo,
          registro.entidadId,
          registro.detalle,
          registro.actorEmail,
          registro.actorUid,
        ].join(" ")
      );

      const matchesEntidadTipo =
        entidadTipoFiltro === "todos" || registro.entidadTipo === entidadTipoFiltro;
      const matchesEntidadId =
        !entidadIdFiltro.trim() ||
        safeText(registro.entidadId || "").includes(safeText(entidadIdFiltro));

      return (
        matchesModule &&
        matchesAction &&
        matchesActor &&
        matchesStart &&
        matchesEnd &&
        matchesEntidadTipo &&
        matchesEntidadId &&
        (!term || haystack.includes(term))
      );
    });
  }, [actionFilter, actorFilter, fechaFin, fechaInicio, moduleFilter, registros, search, entidadTipoFiltro, entidadIdFiltro]);

  const getEntityLink = (registro) => {
    const tipo = String(registro.entidadTipo || "").toLowerCase();
    const id = registro.entidadId;
    const clienteId = registro.despues?.clienteId || registro.antes?.clienteId;

    if (!id) return null;
    if (tipo === "cliente") return `/app/cliente/${id}`;
    if (tipo === "deuda" && clienteId) return `/app/cliente/${clienteId}/deudas/${id}`;
    if (tipo === "ticket") return `/app/tickets/${id}`;
    if (tipo === "cxp" || tipo === "cxp_deuda" || tipo === "cuentaporpagar") {
      return `/app/cxp/deudas/${id}`;
    }
    if (tipo === "obra") return `/app/obras/${id}`;
    if (tipo === "propiedad" || tipo === "lote") return `/app/propiedades/${id}`;
    if (tipo === "fraccionamiento") return `/app/propiedades?fracc=${id}`;
    if (tipo === "proyecto") return `/app/proyectos/${id}/drive`;
    if (tipo === "rol" || tipo === "roles" || tipo === "usuario") return "/app/usuarios";
    if (tipo === "promesapago" && clienteId && (registro.despues?.deudaId || registro.antes?.deudaId)) {
      return `/app/cliente/${clienteId}/deudas/${registro.despues?.deudaId || registro.antes?.deudaId}`;
    }
    if (tipo === "gestioncobranza" && clienteId && (registro.despues?.deudaId || registro.antes?.deudaId)) {
      return `/app/cliente/${clienteId}/deudas/${registro.despues?.deudaId || registro.antes?.deudaId}`;
    }

    return null;
  };

  const exportarExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("Auditoria");

    sheet.columns = [
      { header: "Fecha", key: "fecha", width: 22 },
      { header: "Accion", key: "accion", width: 28 },
      { header: "Actor", key: "actor", width: 34 },
      { header: "Entidad", key: "entidad", width: 24 },
      { header: "Detalle", key: "detalle", width: 50 },
      { header: "Antes", key: "antes", width: 50 },
      { header: "Despues", key: "despues", width: 50 },
    ];

    sheet.getRow(1).font = { bold: true };

    filtered.forEach((registro) => {
      sheet.addRow({
        fecha: formatDate(registro.fecha),
        accion: registro.accion || "",
        actor: registro.actorEmail || registro.actorUid || "",
        entidad: `${registro.entidadTipo || ""}:${registro.entidadId || ""}`,
        detalle: registro.detalle || "",
        antes: registro.antes ? JSON.stringify(registro.antes) : "",
        despues: registro.despues ? JSON.stringify(registro.despues) : "",
      });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `Auditoria_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  if (!puedeVerAuditoria) {
    return (
      <div className="auditoria-page">
        <section className="auditoria-card auditoria-empty-state">
          <FaUserShield />
          <h2>Sin acceso</h2>
          <p>No tienes permiso para consultar auditoria.</p>
        </section>
      </div>
    );
  }

  return (
    <div className="auditoria-page">
      <section className="auditoria-card">
        <div className="auditoria-header">
          <div>
            <span className="auditoria-eyebrow">Control interno</span>
            <h1>Auditoria</h1>
            <p>Registro reciente de acciones sensibles dentro del sistema.</p>
          </div>

          <button type="button" className="auditoria-refresh" onClick={cargarAuditoria} disabled={loading}>
            <FaRedo />
            {loading ? "Cargando..." : "Actualizar"}
          </button>
          <button type="button" className="auditoria-refresh" onClick={exportarExcel} disabled={loading || filtered.length === 0}>
            <FaFileExcel />
            Exportar Excel
          </button>
        </div>

        <div className="auditoria-summary">
          <div>
            <strong>{registros.length}</strong>
            <span>Registros cargados</span>
          </div>
          <div>
            <strong>{filtered.length}</strong>
            <span>Coincidencias</span>
          </div>
          <div>
            <strong>{modules.length}</strong>
            <span>Modulos auditados</span>
          </div>
        </div>

        <div className="auditoria-filters">
          <label className="auditoria-search">
            <FaSearch />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por usuario, accion, entidad o detalle"
            />
          </label>

          <label>
            <FaFilter />
            <select
              value={moduleFilter}
              onChange={(e) => {
                setModuleFilter(e.target.value);
                setActionFilter("todos");
              }}
            >
              <option value="todos">Todos los modulos</option>
              {modules.map((moduleName) => (
                <option key={moduleName} value={moduleName}>
                  {moduleName}
                </option>
              ))}
            </select>
          </label>

          <label>
            <FaClipboardCheck />
            <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
              <option value="todos">Todas las acciones</option>
              {actions.map((accion) => (
                <option key={accion} value={accion}>
                  {accion}
                </option>
              ))}
            </select>
          </label>

          <label>
            <FaUserShield />
            <select value={actorFilter} onChange={(e) => setActorFilter(e.target.value)}>
              <option value="todos">Todos los actores</option>
              {actors.map((actor) => (
                <option key={actor} value={actor}>
                  {actor}
                </option>
              ))}
            </select>
          </label>

          <label>
            Desde
            <input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
          </label>

          <label>
            Hasta
            <input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} />
          </label>

          <label>
            Tipo de entidad
            <select value={entidadTipoFiltro} onChange={(e) => setEntidadTipoFiltro(e.target.value)}>
              <option value="todos">Todos</option>
              {entidadTipos.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </label>

          <label>
            ID de entidad
            <input
              type="text"
              value={entidadIdFiltro}
              onChange={(e) => setEntidadIdFiltro(e.target.value)}
              placeholder="ID exacto o parte"
            />
          </label>
        </div>

        {error && <div className="auditoria-error">{error}</div>}

        <div className="auditoria-table-wrap">
          <table className="auditoria-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Accion</th>
                <th>Actor</th>
                <th>Entidad</th>
                <th>Detalle</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((registro) => (
                <tr key={registro.id}>
                  <td>{formatDate(registro.fecha)}</td>
                  <td>
                    <span className="audit-action">{registro.accion || "sin_accion"}</span>
                  </td>
                  <td>
                    <strong>{registro.actorEmail || "Usuario sin correo"}</strong>
                    <span>{registro.actorUid || "Sin UID"}</span>
                  </td>
                  <td>
                    <strong>{registro.entidadTipo || "general"}</strong>
                    <span>{registro.entidadId || "Sin ID"}</span>
                    {getEntityLink(registro) && (
                      <Link className="audit-link" to={getEntityLink(registro)}>
                        Abrir
                      </Link>
                    )}
                  </td>
                  <td>
                    <p>{registro.detalle || "Sin detalle"}</p>
                    <AuditDetail label="Antes" value={registro.antes} />
                    <AuditDetail label="Despues" value={registro.despues} />
                  </td>
                </tr>
              ))}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan="5" className="auditoria-empty">
                    No hay registros con esos filtros.
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td colSpan="5" className="auditoria-empty">
                    Cargando auditoria...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default Auditoria;
